import type { DateRange, RangeType } from "rsuite/esm/DateRangePicker/types"
import { startOfMonth, endOfMonth, addMonths, addDays } from "date-fns"
import { boxShadow, colors } from "../styles/design.config"
import styled from "styled-components"
import Warning from "../assets/treasury-warning.svg?react"
import {
  TreasuryFullDocuments,
  TreasuryMerchants,
} from "../store/ducks/treasury.ducks"
import { sortByDate } from "./filesList"
import { HeaderTitleWrapper } from "../components/dropDocuments/StyledDropsComponents"

export const lateDate = addDays(new Date(), 0)
export const allTime: [Date, Date] = [
  new Date(new Date().getFullYear() - 10, 10, 10),
  new Date(),
]

export const isInTreasuryRange = (
  fullDocument: TreasuryFullDocuments,
  range: [Date, Date]
): boolean => {
  if (range === allTime) return true
  const date = fullDocument.document_due_date ?? fullDocument.document_date
  return !date || date <= range[1].toISOString().slice(0, 10)
}

export enum BuySellSortOptionsValues {
  "_",
  "document_type",
  "pa_status",
  "due_date",
  "date",
  "merchants",
  "file_name",
  "amount_tax_excluded",
  "amount_tax_included",
}

export const predefinedRanges: RangeType[] = [
  {
    label: "Selectionner jusqu'au :",
    value: [] as unknown as DateRange,
    placement: "left",
  },
  {
    label: "Mois en cours",
    value: [startOfMonth(new Date()), endOfMonth(new Date())],
    placement: "left",
  },
  {
    label: "30 prochains jours",
    value: [new Date(), addDays(new Date(), 30)],
    placement: "left",
  },
  {
    label: "90 prochains jours",
    value: [new Date(), addDays(new Date(), 90)],
    placement: "left",
  },
  {
    label: "6 prochains mois",
    value: [new Date(), addMonths(new Date(), 6)],
    placement: "left",
  },
  {
    label: "12 prochains mois",
    value: [new Date(), addMonths(new Date(), 12)],
    placement: "left",
  },
  {
    label: "18 prochains mois",
    value: [new Date(), addMonths(new Date(), 18)],
    placement: "left",
  },
  {
    label: "Tout le temps",
    value: allTime,
    placement: "left",
  },
]

export const filterDocuments = (
  search: string,
  fullDocuments: TreasuryFullDocuments[]
): TreasuryFullDocuments[] => {
  if (!fullDocuments) return []
  return fullDocuments.filter(
    (fullDocument) =>
      String(Math.abs(fullDocument?.amount_tax_included ?? 0))
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      String(Math.abs(fullDocument?.amount_tax_included ?? 0))
        .replace(".", ",")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      fullDocument?.user_file_name
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      fullDocument?.file_name?.toLowerCase().includes(search.toLowerCase()) ||
      fullDocument?.merchant_name?.toLowerCase().includes(search.toLowerCase())
  )
}

export const filterMerchants = (
  search: string,
  merchants: TreasuryMerchants[]
): TreasuryMerchants[] => {
  if (!merchants) return []
  return merchants.filter((merchant) =>
    merchant?.name?.toLowerCase().includes(search.toLowerCase())
  )
}

export const sorterFunction = (
  asc: boolean,
  option: BuySellSortOptionsValues,
  fullDocuments: TreasuryFullDocuments[]
) => {
  const fullDocumentsSorted = [...fullDocuments]
  if (option === BuySellSortOptionsValues?.document_type) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? (a?.document_type ?? "").localeCompare(b?.document_type ?? "")
          : (b?.document_type ?? "").localeCompare(a?.document_type ?? "")
    )
  } else if (option === BuySellSortOptionsValues?.due_date) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? sortByDate(a.document_due_date, b.document_due_date)
          : sortByDate(b.document_due_date, a.document_due_date)
    )
  } else if (option === BuySellSortOptionsValues?.date) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? sortByDate(a.document_date, b.document_date)
          : sortByDate(b.document_date, a.document_date)
    )
  } else if (option === BuySellSortOptionsValues?.merchants) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? (a?.merchant_name ?? "").localeCompare(b?.merchant_name ?? "")
          : (b?.merchant_name ?? "").localeCompare(a?.merchant_name ?? "")
    )
  } else if (option === BuySellSortOptionsValues?.file_name) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? a?.user_file_name.localeCompare(b?.user_file_name)
          : b?.user_file_name.localeCompare(a?.user_file_name)
    )
  } else if (option === BuySellSortOptionsValues?.amount_tax_included) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? (a.amount_tax_included ?? 0) - (b.amount_tax_included ?? 0)
          : (b.amount_tax_included ?? 0) - (a.amount_tax_included ?? 0)
    )
  } else if (option === BuySellSortOptionsValues?.amount_tax_excluded) {
    fullDocumentsSorted.sort(
      (a: TreasuryFullDocuments, b: TreasuryFullDocuments) =>
        asc
          ? (a.amount_tax_excluded ?? 0) - (b.amount_tax_excluded ?? 0)
          : (b.amount_tax_excluded ?? 0) - (a.amount_tax_excluded ?? 0)
    )
  }
  return fullDocumentsSorted
}

export const sortFullDocumentsByDate = (
  fullDocuments: TreasuryFullDocuments[],
  asc: boolean
) => {
  return [...fullDocuments.sort((a, b) => (asc ? a.id - b.id : b.id - a.id))]
}

export const StyledLink = styled.a`
  color: ${colors.cornflower};
  font-size: 1.75rem;
  font-weight: 500;
  text-decoration: underline !important;
  cursor: pointer;
  word-break: break-all;

  :hover {
    color: ${colors.cornflower};
  }
`
export const TableWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  border-radius: 2.5rem;
  margin: 0 2rem 4rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`
export const WrapperTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 2rem;
`
export const StyledSection = styled.section`
  border-radius: 0 2.5rem 2.5rem;
  background-color: ${colors.white};
  box-shadow: ${boxShadow};
  padding: 0 2rem 4rem;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  overflow: hidden;
`
export const Wrapper = styled.div`
  padding: 0px 4rem 4rem;
  display: flex;
  height: 100%;
  top: 0;
  left: 0;
  box-sizing: border-box;
  touch-action: "auto";
`
export const StyledTotalAmount = styled.div`
  display: flex;
  min-width: 26rem;
  max-width: 42rem;
  justify-content: center;
  align-items: center;
  padding-right: 1rem;
`
export const StyledFullDocumentCount = styled.div`
  display: flex;
  min-width: 8rem;
  max-width: 20rem;
  justify-content: center;
  align-items: center;
  padding-right: 1rem;
`
export const WrapperCalendar = styled.div`
  display: flex;
  width: 400px;
  height: 100%;
`
export interface FisrItemProps {
  isFirstColumn?: boolean
  isLastColumn?: boolean
  marginTop?: string
  flexDirection?: string
  alignItems?: string
}
export const StyledCenter = styled.div<FisrItemProps>`
  justify-content: ${(props) =>
    props.isFirstColumn
      ? "flex-start"
      : props.isLastColumn
      ? "flex-end"
      : "center"};
  display: flex;
  width: 90%;
  align-items: ${(props) => (props.alignItems ? props.alignItems : "center")};
  margin-right: ${(props) => (props.isLastColumn ? "10%" : "")};
  margin-left: ${(props) => (props.isFirstColumn ? "10%" : "")};
  flex-direction: ${(props) =>
    props.flexDirection ? props.flexDirection : "row"};
`
export const StyledWarning = styled(Warning)`
  & path {
    fill: ${colors.amaranth};
  }
`
export const TreasuryHeaderWrapper = styled(HeaderTitleWrapper)`
  padding: 0;
`
export const UserColumn = styled.div`
  word-break: break-all;
`
